import {
	Button,
	ButtonUtility,
	FormFieldControl,
	GroupMultiSelect,
	Input,
	Modal,
	ModalOverlay,
	Select,
	ToastCustom,
} from "@components";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ApiErrorResponse } from "@lib";
import { XClose } from "@untitledui/icons";
import { maskOdometer } from "@utils";
import type { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { CarIcon } from "@/components/ui/icons/CarIcon";
import { veiculosNovo } from "@/services/analytics/events";
import { useGroupsQuery } from "@/shared/hooks";
import {
	bodyTypeMap,
	engineTypeMap,
	handleBackendValidationErrors,
	transmissionTypeMap,
} from "../handlers";
import { useCreateVehicle, useUpdateVehicle } from "../query";
import { type VehicleFormSchema, vehicleFormSchema } from "../schemas";
import type { VehicleApiResponse, VehicleForm } from "../types";

type VehicleFormMode = "create" | "edit";

type VehicleFormModalProps = {
	mode: VehicleFormMode;
	isOpen: boolean;
	onClose: () => void;
	initialData?: VehicleApiResponse;
	onSuccess?: () => void;
};

const bodyTypeOptions = Object.entries(bodyTypeMap).map(([id, label]) => ({
	id,
	label,
}));

const transmissionTypeOptions = Object.entries(transmissionTypeMap).map(
	([id, label]) => ({ id, label })
);

const engineTypeOptions = Object.entries(engineTypeMap).map(([id, label]) => ({
	id,
	label,
}));

const emptyFormValues: VehicleFormSchema = {
	plate: "",
	description: "",
	brand: "",
	model: "",
	modelYear: "",
	manufactureYear: "",
	odometer: "",
	color: "",
	chassisNumber: "",
	renavam: "",
	bodyType: "",
	transmissionType: "",
	engineType: "",
	group_id: null,
};

const toFormString = (value: unknown): string =>
	value !== null && value !== 0 ? String(value) : "";

const mapVehicleToFormValues = (
	vehicle?: VehicleApiResponse | null
): VehicleFormSchema => {
	if (!vehicle) {
		return emptyFormValues;
	}

	return {
		plate: toFormString(vehicle.plate).replace(/[^A-Z0-9]/gi, ""),
		description: toFormString(vehicle.description),
		brand: toFormString(vehicle.brand),
		model: toFormString(vehicle.model),
		modelYear: toFormString(vehicle.modelYear),
		manufactureYear: toFormString(vehicle.manufactureYear),
		odometer: toFormString(vehicle.odometer),
		color: toFormString(vehicle.color),
		chassisNumber: toFormString(vehicle.chassisNumber),
		renavam: toFormString(vehicle.renavam),
		bodyType: toFormString(vehicle.bodyType),
		transmissionType: toFormString(vehicle.transmissionType),
		engineType: toFormString(vehicle.engineType),
		group_id: vehicle.group?.id ?? null,
	};
};
const trimmed = (value: unknown): string => String(value ?? "").trim();

const toInt = (value: unknown): number | undefined => {
	const parsed = Number.parseInt(String(value ?? ""), 10);
	return Number.isNaN(parsed) ? undefined : parsed;
};

/** Monta o payload alinhado com CreateVehicleRequest do backend (Plate, Brand, Model, ..., BodyType?, TransmissionType?, EngineType?, ModelYear?, ManufactureYear?, Odometer?). */
const toVehicleFormData = (values: VehicleFormSchema): VehicleForm => {
	const modelYear = toInt(values.modelYear);
	const manufactureYear = toInt(values.manufactureYear);
	const odometer = toInt(values.odometer);
	const bodyType = toInt(values.bodyType);
	const transmissionType = toInt(values.transmissionType);
	const engineType = toInt(values.engineType);
	return {
		plate: trimmed(values.plate),
		brand: trimmed(values.brand) || undefined,
		model: trimmed(values.model) || undefined,
		description: trimmed(values.description) || undefined,
		chassisNumber: trimmed(values.chassisNumber) || undefined,
		renavam: trimmed(values.renavam) || undefined,
		bodyType: bodyType ?? null,
		color: trimmed(values.color) || undefined,
		transmissionType: transmissionType ?? null,
		engineType: engineType ?? null,
		modelYear: modelYear ?? undefined,
		manufactureYear: manufactureYear ?? undefined,
		odometer: odometer ?? undefined,
		group_id: values.group_id ?? null,
	};
};

export const VehicleFormModal = ({
	mode,
	isOpen,
	onClose,
	initialData,
	onSuccess,
}: VehicleFormModalProps) => {
	const {
		control,
		handleSubmit,
		reset,
		setError,
		formState: { errors, isSubmitting },
	} = useForm<VehicleFormSchema>({
		resolver: zodResolver(vehicleFormSchema),
		defaultValues:
			mode === "edit" ? mapVehicleToFormValues(initialData) : emptyFormValues,
	});
	const [isOdometerFocused, setIsOdometerFocused] = useState(false);

	const { data: groups = [], isLoading: isGroupsLoading } = useGroupsQuery({
		enabled: isOpen,
	});

	const getOdometerDisplayValue = (value: string): string => {
		const formattedValue = maskOdometer(value);
		const odometerWithKmSuffix = formattedValue ? `${formattedValue} km` : "";
		return isOdometerFocused ? formattedValue : odometerWithKmSuffix;
	};

	const createVehicleMutation = useCreateVehicle();
	const updateVehicleMutation = useUpdateVehicle();

	useEffect(() => {
		if (isOpen) {
			reset(
				mode === "edit" ? mapVehicleToFormValues(initialData) : emptyFormValues
			);
		}
	}, [isOpen, mode, initialData, reset]);

	const isLoading =
		createVehicleMutation.isPending || updateVehicleMutation.isPending;

	const handleClose = () => {
		reset(emptyFormValues);
		createVehicleMutation.reset();
		updateVehicleMutation.reset();
		onClose();
	};

	const handleSubmitError = (error: unknown) => {
		handleBackendValidationErrors(error, setError);

		const axiosError = error as AxiosError<{ error: ApiErrorResponse }>;
		const fieldsErrors = axiosError.response?.data?.error?.fieldsErrors;

		if (fieldsErrors) {
			return;
		}

		toast(
			ToastCustom({
				title:
					mode === "create"
						? "Erro ao cadastrar veículo"
						: "Erro ao atualizar veículo",
				variant: "error",
			})
		);
	};

	const onSubmit = async (values: VehicleFormSchema) => {
		const payload = toVehicleFormData(values);

		try {
			if (mode === "create") {
				const novoVeiculo = await createVehicleMutation.mutateAsync(payload);
				veiculosNovo(novoVeiculo.id, novoVeiculo.plate);
			} else if (mode === "edit" && initialData) {
				await updateVehicleMutation.mutateAsync({
					id: initialData.id.toString(),
					data: payload,
				});
			}

			onSuccess?.();
			handleClose();
		} catch (error) {
			handleSubmitError(error);
		}
	};

	const primaryLabel = mode === "create" ? "Adicionar" : "Atualizar";

	const title = mode === "create" ? "Novo veículo" : "Características gerais";
	const description =
		mode === "create"
			? "Cadastre um novo veículo para a frota"
			: "Atualize os dados do seu veículo";

	return (
		<ModalOverlay
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					handleClose();
				}
			}}
		>
			<Modal className="relative mx-auto h-[calc(100vh-10rem)] max-w-[708px]! gap-0 overflow-hidden rounded-2xl bg-bg-primary p-0">
				<div className="flex h-full flex-col">
					<div className="flex flex-row items-center gap-4 border-b border-b-border-secondary border-solid bg-bg-primary p-6 pb-5">
						<div className="flex items-center justify-center rounded-[10px] border border-primary border-solid p-3">
							<CarIcon className="size-6 text-secondary" />
						</div>
						<div className="flex flex-col gap-0.5 text-left">
							<h1 className="font-semibold text-base text-gray-900">{title}</h1>
							<p className="text-gray-600 text-sm">{description}</p>
						</div>
					</div>

					<ButtonUtility
						className="absolute top-7 right-5 z-10"
						color="tertiary"
						icon={<XClose className="size-6 text-quaternary" />}
						onClick={handleClose}
						tooltip="Fechar"
					/>

					<div className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto px-6 pt-5 pb-8">
						<Controller
							control={control}
							name="plate"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.plate?.message}
									htmlFor="plate"
									isRequired
									label="Placa"
								>
									<Input
										error={!!errors.plate}
										id="plate"
										maxLength={7}
										onChange={(event) => {
											const raw = event.target.value
												.toUpperCase()
												.replace(/[^A-Z0-9]/g, "")
												.slice(0, 7);
											field.onChange(raw);
										}}
										placeholder="ABC1234"
										value={field.value}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="model"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.model?.message}
									htmlFor="model"
									label="Modelo"
								>
									<Input
										error={!!errors.model}
										id="model"
										maxLength={30}
										placeholder="Ex: Corolla"
										{...field}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="brand"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.brand?.message}
									htmlFor="brand"
									label="Marca"
								>
									<Input
										error={!!errors.brand}
										id="brand"
										maxLength={30}
										placeholder="Ex: Toyota"
										{...field}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="description"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.description?.message}
									htmlFor="description"
									label="Descrição"
								>
									<Input
										error={!!errors.description}
										id="description"
										maxLength={100}
										placeholder="Descrição do veículo"
										{...field}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="modelYear"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.modelYear?.message}
									htmlFor="modelYear"
									label="Ano do modelo"
								>
									<Input
										error={!!errors.modelYear}
										id="modelYear"
										inputMode="numeric"
										maxLength={4}
										onChange={(event) => {
											const numericOnly = event.target.value
												.replace(/\D/g, "")
												.slice(0, 4);
											field.onChange(numericOnly);
										}}
										placeholder="2021"
										value={field.value}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="manufactureYear"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.manufactureYear?.message}
									htmlFor="manufactureYear"
									label="Ano de fabricação"
								>
									<Input
										error={!!errors.manufactureYear}
										id="manufactureYear"
										inputMode="numeric"
										maxLength={4}
										onChange={(event) => {
											const numericOnly = event.target.value
												.replace(/\D/g, "")
												.slice(0, 4);
											field.onChange(numericOnly);
										}}
										placeholder="2020"
										value={field.value}
									/>
								</FormFieldControl>
							)}
						/>

						{mode !== "edit" && (
							<Controller
								control={control}
								name="odometer"
								render={({ field }) => {
									const displayValue = getOdometerDisplayValue(
										field.value ?? ""
									);
									return (
										<FormFieldControl
											className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
											error={errors.odometer?.message}
											htmlFor="odometer"
											label="Hodômetro (km)"
										>
											<Input
												error={!!errors.odometer}
												id="odometer"
												inputMode="numeric"
												maxLength={7}
												onBlur={() => {
													setIsOdometerFocused(false);
												}}
												onChange={({ target }) => {
													const newValue = target.value;
													// Remove tudo que não for dígito (pontos serão adicionados pela máscara)
													// Remove também " km" caso o usuário tenha digitado
													const onlyNumbers = newValue.replace(/\D/g, "");

													// Limita a 6 caracteres
													if (onlyNumbers.length > 6) {
														return;
													}

													field.onChange(onlyNumbers);
												}}
												onFocus={() => {
													setIsOdometerFocused(true);
												}}
												placeholder="45.000"
												value={displayValue}
											/>
										</FormFieldControl>
									);
								}}
							/>
						)}

						<Controller
							control={control}
							name="color"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.color?.message}
									htmlFor="color"
									label="Cor predominante"
								>
									<Input
										error={!!errors.color}
										id="color"
										maxLength={30}
										placeholder="Ex: Preto"
										{...field}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="chassisNumber"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.chassisNumber?.message}
									htmlFor="chassisNumber"
									label="Chassi"
								>
									<Input
										error={!!errors.chassisNumber}
										id="chassisNumber"
										maxLength={17}
										placeholder="9BWHE21J26C404230"
										{...field}
										onChange={(event) => {
											const raw = event.target.value.toUpperCase()?.trim();
											field.onChange(raw);
										}}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="renavam"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.renavam?.message}
									htmlFor="renavam"
									label="Renavam"
								>
									<Input
										error={!!errors.renavam}
										id="renavam"
										inputMode="numeric"
										maxLength={11}
										minLength={9}
										onChange={(event) => {
											const numericOnly = event.target.value.replace(/\D/g, "");
											field.onChange(numericOnly);
										}}
										placeholder="00000000000"
										value={field.value}
									/>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="bodyType"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.bodyType?.message}
									htmlFor="bodyType"
									label="Carroceria"
								>
									<Select
										aria-labelledby="bodyType"
										className="w-full"
										items={bodyTypeOptions}
										onChange={(key) => {
											if (key === null || key === undefined) {
												field.onChange("");
												return;
											}
											field.onChange(String(key));
										}}
										placeholder="Selecione"
										value={field.value === "" ? null : field.value}
									>
										{(item) => (
											<Select.Item id={item.id} textValue={item.label}>
												{item.label}
											</Select.Item>
										)}
									</Select>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="transmissionType"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.transmissionType?.message}
									htmlFor="transmissionType"
									label="Câmbio"
								>
									<Select
										aria-labelledby="transmissionType"
										className="w-full"
										isInvalid={!!errors.transmissionType}
										items={transmissionTypeOptions}
										onChange={(key) => {
											if (key === null || key === undefined) {
												field.onChange("");
												return;
											}
											field.onChange(String(key));
										}}
										placeholder="Selecione"
										value={field.value === "" ? null : field.value}
									>
										{(item) => (
											<Select.Item id={item.id} textValue={item.label}>
												{item.label}
											</Select.Item>
										)}
									</Select>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="engineType"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.engineType?.message}
									htmlFor="engineType"
									label="Motor"
								>
									<Select
										aria-labelledby="engineType"
										className="w-full"
										isInvalid={!!errors.engineType}
										items={engineTypeOptions}
										onChange={(key) => {
											if (key === null || key === undefined) {
												field.onChange("");
												return;
											}
											field.onChange(String(key));
										}}
										placeholder="Selecione"
										value={field.value === "" ? null : field.value}
									>
										{(item) => (
											<Select.Item id={item.id} textValue={item.label}>
												{item.label}
											</Select.Item>
										)}
									</Select>
								</FormFieldControl>
							)}
						/>

						<Controller
							control={control}
							name="group_id"
							render={({ field }) => (
								<FormFieldControl
									className="xs:grid xs:grid-cols-[160px_1fr] xs:grid-rows-1 xs:items-start xs:gap-8"
									error={errors.group_id?.message}
									htmlFor="group_id"
									label="Grupo"
								>
									<GroupMultiSelect
										groups={groups}
										isInvalid={!!errors.group_id}
										isLoading={isGroupsLoading}
										isMulti={false}
										onChange={(ids) => {
											field.onChange(ids[0] ?? null);
										}}
										placeholder="Digite ou selecione o grupo"
										value={
											field.value !== null && field.value !== undefined
												? [field.value]
												: []
										}
									/>
								</FormFieldControl>
							)}
						/>
					</div>

					<div className="flex flex-row justify-end gap-3 border-t border-t-border-secondary border-solid bg-bg-primary p-6">
						<Button
							className="flex-1"
							disabled={isSubmitting || isLoading}
							onClick={handleClose}
							size="lg"
							type="button"
							variant="secondary"
						>
							Cancelar
						</Button>
						<Button
							className="flex-1"
							disabled={isSubmitting || isLoading}
							onClick={handleSubmit(onSubmit)}
							size="lg"
							type="button"
						>
							{primaryLabel}
						</Button>
					</div>
				</div>
			</Modal>
		</ModalOverlay>
	);
};
